/**
 * JobStatusTable - Lista os jobs de conectividade (KORA) recentes com seu status.
 *
 * Útil para acompanhar o processamento assíncrono: ao configurar um dispositivo
 * NB2 LoRa, um job `create-device` é criado e enviado ao Pulsar. Um worker externo
 * consome a fila, fala com a KORA e marca o status do job. Esta tabela mostra a
 * transição PENDING → PROCESSING → SUCCESS/FAILED, com auto-refresh.
 */

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AlertCircle, CheckCircle, Clock, Loader2, RefreshCcw } from 'lucide-react';
import { type Job, JobStatus, loraApi } from '@/api/connectivity/lora';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const STATUS_CONFIG: Record<
  JobStatus,
  {
    label: string;
    variant: 'info-subtle' | 'success-subtle' | 'destructive-subtle' | 'secondary-subtle';
    icon: typeof Clock;
  }
> = {
  [JobStatus.JS_PENDING]: { label: 'Pendente', variant: 'secondary-subtle', icon: Clock },
  [JobStatus.JS_PROCESSING]: { label: 'Processando', variant: 'info-subtle', icon: Loader2 },
  [JobStatus.JS_SUCCESS]: { label: 'Concluído', variant: 'success-subtle', icon: CheckCircle },
  [JobStatus.JS_FAILED]: { label: 'Falhou', variant: 'destructive-subtle', icon: AlertCircle },
};

function JobStatusBadge({ status }: { status: JobStatus }) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG[JobStatus.JS_PENDING];
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className={status === JobStatus.JS_PROCESSING ? 'h-3 w-3 animate-spin' : 'h-3 w-3'} />
      {config.label}
    </Badge>
  );
}

function formatDate(value?: string): string {
  if (!value) return '--';
  try {
    return format(new Date(value), 'dd/MM/yyyy HH:mm:ss');
  } catch {
    return '--';
  }
}

export function JobStatusTable() {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['connectivity-jobs', 'recent'],
    queryFn: () => loraApi.findJobs({ page: 1, limit: 10 }),
    // Auto-refresh: jobs são processados de forma assíncrona; atualizamos
    // periodicamente para refletir a transição de status sem recarregar a página.
    refetchInterval: 5000,
    staleTime: 0,
  });

  const jobs: Job[] = data?.docs ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Jobs recentes</h2>
          <p className="text-sm text-muted-foreground">
            Acompanhe o processamento dos cadastros e sincronizações enviados à KORA.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCcw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Atualizar
        </Button>
      </div>

      <div className="border border-border rounded-md w-full overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Criado em</TableHead>
              <TableHead>Concluído em</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Carregando jobs...
                </TableCell>
              </TableRow>
            ) : jobs.length ? (
              jobs.map((job) => (
                <TableRow key={job.id} className="hover:bg-background">
                  <TableCell>
                    <JobStatusBadge status={job.status} />
                  </TableCell>
                  <TableCell className="font-mono text-sm">{job.payload?.serial ?? '--'}</TableCell>
                  <TableCell className="text-sm">{job.payload?.job_category ?? '--'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(job.created_at)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(job.completed_at)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Nenhum job encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default JobStatusTable;
